import { useEffect, useState } from "react";
import { normalizeHexColor } from "@/utils/pageBackgrounds";

export interface ThemePalette {
  background: string;
  accent: string;
  accent2: string;
  text: string;
  seed: string | null;
}

export const FALLBACK_THEME: ThemePalette = { background: "#0a0a0a", accent: "#a99172", accent2: "#d9c08a", text: "#f4efe6", seed: null };

function cssColor(value: string): string | null {
  const text = value.trim().toLowerCase();
  const hex = normalizeHexColor(text.startsWith("#") ? text.slice(0, 7) : "");
  if (hex) return hex;
  const rgb = text.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
  if (!rgb) return null;
  return `#${[rgb[1], rgb[2], rgb[3]].map((part) => Math.max(0, Math.min(255, Math.round(Number(part)))).toString(16).padStart(2, "0")).join("")}`;
}

export function readThemePalette(): ThemePalette {
  if (typeof document === "undefined") return FALLBACK_THEME;
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string) => cssColor(styles.getPropertyValue(name));
  return {
    background: read("--backgroundColor") ?? FALLBACK_THEME.background,
    accent: read("--logoColor") ?? FALLBACK_THEME.accent,
    accent2: read("--logoColor2") ?? FALLBACK_THEME.accent2,
    text: read("--textColor") ?? FALLBACK_THEME.text,
    seed: read("--accentColor") ?? read("--color2"),
  };
}

const same = (a: ThemePalette, b: ThemePalette) => a.background === b.background && a.accent === b.accent && a.accent2 === b.accent2 && a.text === b.text && a.seed === b.seed;

export function useThemePalette(enabled = true): ThemePalette {
  const [palette, setPalette] = useState<ThemePalette>(() => (enabled ? readThemePalette() : FALLBACK_THEME));
  useEffect(() => {
    if (!enabled) return;
    const refresh = () => {
      const next = readThemePalette();
      setPalette((current) => (same(current, next) ? current : next));
    };
    refresh();
    const observer = new MutationObserver(refresh);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["style", "class", "data-theme"] });
    return () => observer.disconnect();
  }, [enabled]);
  return palette;
}
